import React, { useMemo } from 'react';
import format from 'date-fns/format';
import { useParams } from 'react-router-dom';
import PrimaryDetails from './PrimaryDetails';
import { useAuthUser } from 'hooks/useAuthUser';
import { getProfileUser, getUserByEmail } from 'api/users/api';
import { useQuery } from 'react-query';
import { plainTextToPhoneNumber } from 'shared/util/util';
import Avatar from 'components/Avatar';
import clsx from 'clsx';
import { Key } from 'react-ionicons';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { useChangePasswordMutation } from 'hooks/useChangePasswordMutation';
import { TokenUserRoles } from 'api/users/schemas/Users';
import { usePermissions } from 'hooks/usePermissions';
import Divider from 'components/Divider';
import UserAccountDetails from 'screens/UserAccounts/UserAccountsDetails';
import { UserActions } from './UserActions';
import {
  getDisplayStatus,
  indicatorBgColor,
} from 'components/UserStatusColumn';
import RoleTag from 'screens/RolesPermissions/CustomRoles/RoleTag';

const UserDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const { email, companyName, grandparentMacnum } = useAuthUser();
  const { isSuperAdmin: hasTokenSuperAdminRole } = usePermissions();

  const { data: profileUser } = useQuery(
    ['user', 'profile'],
    () => getProfileUser(),
    {
      refetchOnWindowFocus: false,
      enabled: !id,
    },
  );
  const { data: user } = useQuery(
    ['user', id],
    () => getUserByEmail({ email: id! }),
    {
      refetchOnWindowFocus: false,
      enabled: !!id,
    },
  );

  const data = id
    ? {
        ...user,
        email: id,
      }
    : {
        ...profileUser,
        email,
      };

  const dateAdded = data?.invited_at || data?.created_at;

  const changePasswordMutation = useChangePasswordMutation();

  const role = useMemo(() => data?.roles?.[0], [data?.roles]);

  const userStatus = id ? data.global_status : data.status;

  return (
    <div className="flex w-full flex-col items-start justify-start gap-6">
      <div className="col-span-12 flex w-full items-center justify-between gap-12 rounded bg-background-base-surface-2 px-6 py-4">
        <div className="flex w-full items-center justify-start gap-3">
          <span
            className={clsx(
              `h-4 w-4 rounded-full`,
              indicatorBgColor(userStatus!),
            )}
          ></span>
          <h2 className="flex gap-6 text-xl font-bold text-content-base-default">
            {getDisplayStatus(userStatus!)}
            <UserActions status={userStatus!} id={data.id!} />
          </h2>
        </div>
        <div className="flex w-full flex-col items-end justify-start">
          <p className="text-base font-bold text-content-base-subdued">
            Date Added
          </p>
          <h2 className="text-base font-bold text-content-base-default">
            {dateAdded ? format(new Date(dateAdded), 'MM/dd/yyyy') : ' -- '}
          </h2>
        </div>
      </div>
      <div className="grid h-full w-full grid-cols-12 overflow-hidden rounded">
        <div className="col-span-4 flex h-full w-full flex-col items-start justify-start gap-4 bg-background-base-surface-3 px-8 py-6">
          <h2 className="text-2xl font-bold text-content-base-default">
            User Details
          </h2>
          <Avatar width="w-20" height="h-20" name={data?.name ?? 'N/A'} />
          <div className="flex w-full flex-col items-start justify-start gap-4">
            <PrimaryDetails label={'Name'} value={data?.name ?? 'N/A'} />
            <PrimaryDetails label={'Email'} value={data.email ?? 'N/A'} />
            <PrimaryDetails
              label={'Secondary email'}
              value={data?.secondary_email ?? 'N/A'}
            />
            <PrimaryDetails
              label={'Phone number'}
              value={
                data?.phone_number
                  ? plainTextToPhoneNumber(data.phone_number)
                  : 'No phone number'
              }
            />
            {data?.secondary_phone_number && (
              <PrimaryDetails
                label={'Secondary phone number'}
                value={plainTextToPhoneNumber(data?.secondary_phone_number)}
              />
            )}
            <PrimaryDetails label={'Company'} value={companyName ?? 'N/A'} />
            <PrimaryDetails
              label={'Company account #'}
              value={grandparentMacnum}
            />
            <PrimaryDetails
              label={'Company title'}
              value={data?.company_title ? data?.company_title : 'N/A'}
            />
            <PrimaryDetails
              label={'Department'}
              value={data?.department ? data?.department : 'N/A'}
            />
          </div>
          {!id && (
            <GraniteButton
              variant="secondary"
              size="large"
              className="mt-10 box-border w-full"
              onClick={() => changePasswordMutation.mutate()}
            >
              Reset password <Key />
            </GraniteButton>
          )}
        </div>
        <div className="col-span-8 flex h-full w-full flex-col items-start justify-start gap-4 bg-background-base-surface-2 px-8 pb-12 pt-6">
          <div className="flex w-full flex-col items-start justify-start gap-4">
            <h2 className="text-2xl font-semibold text-content-base-default">
              Role & permissions
            </h2>
            <div className="flex flex-col gap-2">
              <p className="font-bold text-content-base-subdued">Role</p>
              <div className="flex w-full items-start justify-start gap-4">
                <div>
                  <RoleTag
                    name={
                      hasTokenSuperAdminRole && !id
                        ? TokenUserRoles.SUPER_ADMIN
                        : role?.name ?? ''
                    }
                    color={
                      hasTokenSuperAdminRole && !id ? '' : role?.color ?? ''
                    }
                  />
                </div>
                <p className="w-full text-base font-bold text-content-base-default">
                  <>
                    {role?.name === TokenUserRoles.VIEWER &&
                      'This user will be limited to read only permissions'}
                    {[
                      TokenUserRoles.COMPANY_ADMIN,
                      TokenUserRoles.SUPER_ADMIN,
                    ].includes(role?.name as TokenUserRoles) &&
                      'This user will have full read & write permissions and the ability to manage users'}
                    {role?.name === TokenUserRoles.WELCOME_USER &&
                      'Pending role assignment'}
                  </>
                </p>
              </div>
            </div>
          </div>
          {id && (
            <>
              <Divider className="w-full" />
              <UserAccountDetails
                subGroups={
                  data.sub_groups?.map((subGroup) => ({
                    id: subGroup.profile.cw_id,
                    name: subGroup.profile.display_name,
                  })) || []
                }
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetails;
